import {
  CalendarToday as CalendarTodayIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { CustomButton } from 'src/components/button/Button';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { SimpleDialog } from 'src/components/modal';
import { useModal } from 'src/hooks';
import { ComponentTypes, IStatus } from 'src/interfaces';
import { getComponentDisplayName, lang } from 'src/utils';

import { getUpdatedComponentConfig } from './UpdateComponentSettings.helper';
import { IUpdateSettingsProps } from './UpdateComponentSettings.interface';
import {
  CheckBoxContainer,
  Container,
  InputDate,
  InputTextField,
  Label,
  OOContanier,
  OOSubText,
  OOTitle,
} from './UpdateComponentSettings.styled';

export const UpdateComponentSettings: React.FC<IUpdateSettingsProps> = ({
  handleClose,
  componentDetails,
  componentConfig,
  onUpdate,
  products,
}) => {
  const [expiry, setExpiry] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [componentName, setComponentName] = useState<string>('');
  const [ooPermission, setooPermission] = useState<boolean>(false);
  const [categoryUpdated, setCategoryUpdated] = useState<boolean>(false);
  const [categoryAdded, setCategoryAdded] = useState<string[]>(
    componentDetails.product_category_id.map(String),
  );
  const [componentTagsAdded, setComponentTagsAdded] = useState<string[]>(
    componentDetails.component_tag_id.map(String),
  );
  const updateComponentModal = useModal();

  useEffect(() => {
    setExpiry(componentDetails.end_date);
    setStartDate(componentDetails.start_date);
    setComponentName(componentDetails.name);
    if (componentDetails.usable_as_override !== undefined) {
      setooPermission(componentDetails.usable_as_override);
    }
  }, [componentDetails]);

  const handleSelect = (key: string, value: string | null) => {
    if (key === 'expiryDate') {
      setExpiry(value);
    }
  };

  const handleSelectStartDate = (key: string, value: string | null) => {
    if (key === 'startDate') {
      setStartDate(value);
    }
  };

  const handleUpdateClick = async () => {
    onUpdate(
      getUpdatedComponentConfig(
        products,
        categoryAdded.map(Number),
        componentTagsAdded.map(Number),
        componentDetails,
        componentConfig,
        componentName,
        ooPermission,
        expiry,
        startDate,
      ),
    );
    setCategoryUpdated(false);
  };

  const disablePastDates = (date: dayjs.Dayjs) => {
    return date.isBefore(dayjs().startOf('day').add(1, 'day'));
  };
  const handleClear = (e: any) => {
    e.stopPropagation();
    setExpiry(null);
    setCategoryUpdated(false);
  };

  const handleClearStartDate = (e: any) => {
    e.stopPropagation();
    setStartDate(null);
    setCategoryUpdated(false);
  };

  const handleCheckBoxChange = () => {
    setStartDate(null);
    setooPermission(!ooPermission);
  };
  const addCategoryForComponent = (categoryId: string[]) => {
    setCategoryUpdated(true);
    setCategoryAdded(categoryId);
  };

  return (
    <Container>
      <DialogTitle>{lang.componentSettings}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Label>{lang.channel}</Label>
            <Label textcolor="charcoal">
              {componentDetails?.layoutType?.name}
            </Label>
          </Grid>
          <Grid item xs={12}>
            <Label>{lang.componentType}</Label>
            <Label textcolor="charcoal">
              {getComponentDisplayName(
                componentDetails.component_type?.name as ComponentTypes,
              )}
            </Label>
          </Grid>
          <Grid item xs={12}>
            <Label>{lang.size}</Label>
            <Label textcolor="charcoal">
              {componentDetails.width} x {componentDetails.height} {`(pixels)`}{' '}
            </Label>
          </Grid>

          <Grid item xs={12}>
            <Label>{`${lang.componentName} *`}</Label>
            <InputTextField
              fullWidth
              variant="outlined"
              size="small"
              value={componentName}
              onBlur={(e: { target: { value: string } }) =>
                setComponentName(e.target.value.trim())
              }
              onChange={(e: { target: { value: string } }) =>
                setComponentName(e.target.value)
              }
            />
          </Grid>

          {componentDetails.component_type?.name &&
            ![
              ComponentTypes.COD,
              ComponentTypes.COD_THANK_YOU,
              ComponentTypes.DISCLAIMER,
            ].includes(componentDetails.component_type.name) && (
              <Grid item xs={12}>
                <FilterDropdownWithSearch
                  name={lang.restrictCategoryText}
                  label={lang.restrictCategoryText}
                  placeholder={lang.addCategoryText}
                  multiline={true}
                  onChange={(value: any) => {
                    addCategoryForComponent(value);
                  }}
                  value={categoryAdded}
                  badgeOptions={{
                    align: 'top',
                  }}
                />
              </Grid>
            )}
          <Grid item xs={12}>
            <FilterDropdownWithSearch
              name={lang.componentTag}
              label={lang.componentTag}
              placeholder={lang.addComponentTag}
              multiline={true}
              onChange={(value: any) => {
                setComponentTagsAdded(value);
              }}
              value={componentTagsAdded}
              badgeOptions={{
                align: 'bottom',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Label>{lang.expiryDate}</Label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <InputDate
                shouldDisableDate={disablePastDates}
                format="MM-DD-YYYY"
                inputFormat="MM-DD-YYYY"
                ampm={false}
                value={dayjs(expiry)}
                onChange={(value: dayjs.Dayjs | null) => {
                  handleSelect(
                    'expiryDate',
                    value && value.isValid()
                      ? value.format('YYYY-MM-DD')
                      : null,
                  );
                }}
                minDate={startDate ? dayjs(startDate).add(1, 'day') : undefined}
                slotProps={{
                  textField: {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {expiry && (
                            <IconButton onClick={handleClear}>
                              <CancelIcon />
                            </IconButton>
                          )}
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <OOTitle>{lang.ownerOperator.ownerOperatorHeader}</OOTitle>
            <OOContanier>
              <OOSubText>{lang.permissions}</OOSubText>
              <CheckBoxContainer>
                <Checkbox
                  checked={ooPermission}
                  onChange={handleCheckBoxChange}
                />
                <Typography>
                  {lang.ownerOperator.allowOwnerOperatorInfoText}
                </Typography>
              </CheckBoxContainer>
            </OOContanier>
          </Grid>
          {componentDetails.component_type?.name ===
            ComponentTypes.PROMOTIONAL_SPACE && (
            <Grid item xs={12}>
              <Label $disabled={ooPermission === false}>
                {lang.softSellDate}
              </Label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <InputDate
                  shouldDisableDate={disablePastDates}
                  format="MM-DD-YYYY"
                  inputFormat="MM-DD-YYYY"
                  ampm={false}
                  disabled={ooPermission === false}
                  value={dayjs(startDate)}
                  maxDate={
                    expiry ? dayjs(expiry).subtract(1, 'day') : undefined
                  }
                  onChange={(value: dayjs.Dayjs | null) => {
                    handleSelectStartDate(
                      'startDate',
                      value && value.isValid()
                        ? value.format('YYYY-MM-DD')
                        : null,
                    );
                  }}
                  slotProps={{
                    textField: {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {startDate && (
                              <IconButton onClick={handleClearStartDate}>
                                <CancelIcon />
                              </IconButton>
                            )}
                            <CalendarTodayIcon />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton variant="text" onClick={handleClose}>
          {lang.cancel}
        </CustomButton>
        <Button
          variant="contained"
          color="primary"
          disabled={!componentName || componentDetails.status !== IStatus.DRAFT}
          onClick={() =>
            categoryUpdated
              ? updateComponentModal.onOpenModal()
              : handleUpdateClick()
          }>
          {lang.update}
        </Button>
      </DialogActions>

      <SimpleDialog
        open={updateComponentModal.open}
        cancelText={lang.cancel}
        confirmText={lang.update}
        title={lang.UpdateComponentSettingsModal.title}
        subTitle={lang.UpdateComponentSettingsModal.subtitle}
        onClose={updateComponentModal.onCloseModal}
        onClickCancel={updateComponentModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={() => handleUpdateClick()}
      />
    </Container>
  );
};
